import { DataType } from '@policygenius/dif-ts-types';

/**
 * @param DPIDs a flat object of DPIDs (including the boolean DPIDs we want to convert)
 * @param booleanDPIDKeys an array of keys where we expect to find boolean DPIDs within `DPIDs`
 *
 * This checks that there's at least one non-nullish value for the expected DPIDs.
 * If any one of those is non-nullish, it uses filter to check for each key in the DPID array,
 * only including that key if it is true, not truthy, for extra safety. Otherwise, it returns
 * undefined as every DPID was nullish.
 */
export const createVirtualMultiChoiceDPID = (
  DPIDs: Record<string, any> | undefined,
  booleanDPIDKeys: string[]
): string[] | undefined => {
  if (booleanDPIDKeys.some((key) => DPIDs?.[key] != null)) {
    return booleanDPIDKeys.filter((key) => DPIDs?.[key] === true);
  }

  return undefined;
};

/**
 * @param virtualDPID the value from a virtual Multi-choice DPID
 * @param booleanDPIDKeys  an array of keys for the desired boolean DPIDs
 * @returns Record of urns as keys with boolean values based on the presence
 *  of those keys in the virtualDPID. In the event the virtualDPID does not
 * have the key, the function returns `false` for the affected key. If the
 * entire virtualDPID is nullish, this function will return undefined for
 * every entry.
 */
export const virtualToBooleanDPIDs = (
  virtualDPID: string[] | undefined,
  booleanDPIDKeys: string[]
): Record<string, boolean> | Record<string, undefined> => {
  if (virtualDPID == null) {
    return booleanDPIDKeys.reduce((acc, key) => ({ ...acc, [key]: undefined }), {});
  }

  return booleanDPIDKeys.reduce((acc, key) => ({ ...acc, [key]: virtualDPID.includes(key) }), {});
};

interface BooleanDPID {
  urn: string;
  value: boolean | undefined;
  dataType: DataType;
}

/**
 * @param DPIDs a flat object of DPIDs (including the boolean DPIDs we want to convert into submission types)
 * @param booleanDPIDKeys an array of keys where we expect to find boolean DPIDs within `DPIDs`
 * @returns an array of Records, ready to be submitted to the DPID data service,
 * containing the `urn`, `value`, and a dataType of `'boolean'`. Will only create
 * records for keys present in `booleanDPIDKeys`. Will skip non-boolean `values`
 * and log an error. If given nullish for `DPIDs`, will return empty array.
 */
export const addDataTypeToBooleanDPIDs = (
  DPIDs: Record<string, any> | undefined,
  booleanDPIDKeys: string[]
): BooleanDPID[] => {
  if (DPIDs == null) {
    return [];
  }

  return booleanDPIDKeys.reduce<BooleanDPID[]>((acc, key) => {
    if (DPIDs[key] == null || typeof DPIDs[key] === 'boolean') {
      return [
        ...acc,
        {
          urn: key,
          dataType: 'boolean',
          value: DPIDs[key] ?? undefined,
        },
      ];
    }
    console.error(`DPID: ${key} does not have a boolean or nullish value`);

    return acc;
  }, []);
};

/**
 * @param virtualDPID the value from a virtual Multi-choice DPID
 * @param booleanDPIDKeys  an array of keys for the desired boolean DPIDs
 * @returns {BooleanDPID[]} an array of records that match the shape for submitting
 * Data Point Values with the `useCreateDataPointValues` hook
 */
export const buildBodyFromVirtualBooleans = (
  virtualDPID: string[] | undefined,
  booleanDPIDKeys: string[]
): BooleanDPID[] => {
  const booleanDPIDs = virtualToBooleanDPIDs(virtualDPID, booleanDPIDKeys);

  return addDataTypeToBooleanDPIDs(booleanDPIDs, booleanDPIDKeys);
};
